/* eslint-disable camelcase */
import env from '@/env';
import { Paths } from '@/pages/routers';
import { logoutUserActions } from '@/redux/actions/auth';
import configureStore from '@/redux/configureStore';
import { authHelpers } from '@/services/auth';
import { getAuthUser } from '@/utils';
import { navigate } from '@reach/router';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import authService from './auth';

type TokenSubscriberCallback = (error: Error | null, accessToken?: string, orgToken?: string) => void;

let sharedFetchAppTokenPromise = authService.getAppAccessToken();
let appAccessToken: string;
let isRefreshingAccessToken = false;
let tokenSubscribers: TokenSubscriberCallback[] = [];

const createAuthorizedApiClient = (baseURL: string, withOrgToken: boolean = false) => {
  const instance = axios.create({
    baseURL,
  });

  const forceLogout = () => {
    const store = configureStore();
    store.dispatch(logoutUserActions.request());
    navigate(`${Paths.Auth}${Paths.Login}`);
  };

  const getAuthBearer = async () => {
    return authHelpers.getAccessToken() || fetchAppAccessToken();
  };

  const fetchOrganisationToken = async (accessToken: string) => {
    try {
      const {
        data: { data },
      } = await axios.get(`${env.api.baseUrl.membership}/users/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const organisationUser = data?.memberships?.filter(
        (org: { roleName: string }) => org.roleName === env.roles.organisationOwner
      );
      const memberShip = organisationUser?.length > 0 ? organisationUser[0] : data?.memberships[0];
      return memberShip.token;
    } catch (error) {
      return undefined;
    }
  };

  const refreshTokens = async () => {
    const refreshToken = await authHelpers.getRefreshToken();
    if (!refreshToken) {
      // use refresh token does not exist, fallback to app token

      const accessToken = await sharedFetchAppTokenPromise;
      authHelpers.clearTokens();
      authHelpers.storeAccessToken(accessToken);
      authHelpers.clearAssetHistory();

      return {
        accessToken,
        orgToken: '',
      };
    }

    const { refresh_token, id_token, access_token } = await authService.refreshToken(refreshToken ?? '');
    let orgToken;
    if (withOrgToken) {
      orgToken = await fetchOrganisationToken(access_token);
      authHelpers.storeOrgToken(orgToken);
    }

    authHelpers.storeIdToken(id_token);
    authHelpers.storeAccessToken(access_token);
    authHelpers.storeRefreshToken(refresh_token);

    return {
      accessToken: await getAuthBearer(),
      orgToken,
    };
  };

  const subscribeTokenRefresh = (cb: TokenSubscriberCallback) => {
    tokenSubscribers.push(cb);
  };

  const onTokenRefreshed = (error: Error | null, newAccessToken?: string, orgToken?: string) => {
    tokenSubscribers.map((cb) => cb(error, newAccessToken, orgToken));
  };

  const fetchAppAccessToken = async () => {
    if (!appAccessToken) {
      appAccessToken = await sharedFetchAppTokenPromise;
    }
    return appAccessToken;
  };

  const onRequest = async (request: AxiosRequestConfig) => {
    const authBearer = await getAuthBearer();
    const orgToken = await authHelpers.getOrgToken();
    if (authBearer) {
      request.headers.Authorization = `Bearer ${authBearer}`;
    }
    if (withOrgToken && orgToken) {
      request.headers['org-token'] = orgToken;
    }

    const authUser = getAuthUser();

    if (!request.headers.organisationId && authUser) {
      request.headers.organisationId = authUser.organisationId;
    }

    return request;
  };

  const onResponseSuccess = (response: AxiosResponse) => {
    return response;
  };

  const onResponseError = async (axiosError: AxiosError) => {
    const { response } = axiosError;
    const originalRequest = axiosError.config;

    if (response?.status === 401 || response?.status === 403) {
      authHelpers.setSessionVerification();
      if (!isRefreshingAccessToken) {
        isRefreshingAccessToken = true;
        sharedFetchAppTokenPromise = authService.getAppAccessToken();

        refreshTokens()
          .then(({ accessToken, orgToken }) => {
            onTokenRefreshed(null, accessToken, orgToken);
          })
          .catch(() => {
            onTokenRefreshed(new Error('Failed to refresh access token'));
            forceLogout();
          })
          .finally(() => {
            isRefreshingAccessToken = false;
            tokenSubscribers = [];
          });
      }

      const retryOriginalRequest = new Promise((resolve, reject) => {
        subscribeTokenRefresh((error: Error | null, newAccessToken?: string, orgToken?: string) => {
          if (error) {
            return reject(error);
          }

          if (orgToken) {
            originalRequest.headers['org-token'] = orgToken;
          }

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return resolve(axios(originalRequest));
        });
      });
      return retryOriginalRequest;
    }

    return Promise.reject(axiosError);
  };

  instance.interceptors.request.use(onRequest);
  instance.interceptors.response.use(onResponseSuccess, onResponseError);

  return instance;
};

export default createAuthorizedApiClient;
